import {
  ComboboxGroup,
  ComboboxGroupLabel,
  ComboboxItem,
  ComboboxList,
  ComboboxProvider,
} from '@ariakit/react';

import { Link } from 'react-router-dom';
import { Icon as MDIicon } from '@mdi/react';
import { mdiCheck, mdiPencil, mdiTrashCanOutline } from '@mdi/js';
import { matchSorter } from 'match-sorter';
import { startTransition, useMemo, useState } from 'react';
import { styled } from 'styled-components';

import IconDash from 'components/icons/IconDashboard';
import { ComboboxSearch, Tooltip, Truncate } from 'components/ui';

const IconDashboard = styled(IconDash)`
  fill: #bd82ce;
  width: 11px;
  height: 11px;
  min-width: 11px;
  min-height: 11px;
  margin: -1px 8px 0 0;
`;

/** checkmark icon displayed next to selected dashboard */
const IconSelected = styled(MDIicon).attrs({
  path: mdiCheck,
})`
  margin-right: 4px;
  padding: 1px;
  width: 18px;
  height: 18px;
`;

const IconEdit = styled(MDIicon).attrs({
  path: mdiPencil,
})`
  padding: 1px;
  width: 18px;
  height: 18px;
`;

const IconDelete = styled(MDIicon).attrs({
  path: mdiTrashCanOutline,
})`
  padding: 1px;
  width: 18px;
  height: 18px;
`;

const IconButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  margin-left: auto;
`;

/** container for the dashboard list */
const Wrapper = styled.div`
  box-shadow:
    0 0 0 1px rgb(17 20 24 / 10%),
    0 2px 4px rgb(17 20 24 / 20%),
    0 8px 24px rgb(17 20 24 / 20%);
  transform: scale(1);
  border-radius: 2px;
  display: flex;
  flex-direction: column;
  max-height: 60vh;
  width: 400px;
  z-index: 20;
  overflow-y: auto;
  position: relative;
`;

const GroupLabel = styled(ComboboxGroupLabel)`
  background-color: #fff;
  color: #1c2127;
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  padding: 5px 7px;
  position: sticky;
  top: 38px;
`;

const ProjectNote = styled.span`
  font-weight: 400;
`;

const MenuWrapper = styled(ComboboxList)`
  padding: 2.5px;
`;

const MenuItem = styled(ComboboxItem)`
  align-items: center;
  border-radius: 2px;
  color: #1c2127;
  display: flex;
  flex-direction: row;
  font-size: 12px;
  justify-content: space-around;
  line-height: 20px;
  padding: 5px 7px;
  text-decoration: none;
  user-select: none;

  ${IconButton} {
    visibility: hidden;
  }

  &:hover,
  &[data-active-item] {
    background-color: rgb(143 153 168 / 15%);

    ${IconButton} {
      visibility: visible;
      pointer-events: auto;
    }
  }

  &[data-state='checked'] {
    background-color: rgb(45 114 210 / 10%);
    color: #215db0;
    padding-left: 5px;
  }
`;

const MenuItemActions = styled.div`
  display: flex;
  margin-left: auto;
  gap: 6px;
`;

/**
 * @param {object} props -
 * @param {string} props.activeId - activeId dashboard id
 * @param {string} props.activeProject - active project key
 * @param {boolean} props.open - popover visibility state (mirrored with the
 *   parent pop-up for aria compliance)
 * @param {Function} props.setOpen - popover visibility callback
 * @param {Array} props.projects - array of projects
 * @param {Function} props.onClickEdit - edit dashboard callback
 * @param {Function} props.onClickDelete - delete dashboard callback
 */
export default function DashboardList({
  activeId,
  activeProject,
  open,
  projects,
  setOpen,
  onClickEdit,
  onClickDelete,
}) {
  const [searchValue, setSearchValue] = useState('');
  const matches = useMemo(() => {
    if (!searchValue) {
      return projects;
    }
    return projects
      .map(({ dashboards, ...project }) => ({
        ...project,
        dashboards: matchSorter(dashboards, searchValue, { keys: ['name'] }),
      }))
      .filter(({ dashboards }) => dashboards.length > 0);
  }, [projects, searchValue]);
  return (
    <Wrapper>
      <ComboboxProvider
        open={open}
        setOpen={setOpen}
        resetValueOnHide
        includesBaseElement={false}
        setValue={(value) => {
          startTransition(() => {
            setSearchValue(value);
          });
        }}
      >
        <ComboboxSearch />
        <MenuWrapper>
          {matches.length > 0 ? (
            matches.map(({ dashboards, ...project }) => (
              <ComboboxGroup key={project.name}>
                <GroupLabel>
                  {project.name}{' '}
                  <ProjectNote>
                    {project.name === activeProject && '(Current project)'}
                  </ProjectNote>
                </GroupLabel>
                {dashboards.length > 0 &&
                  dashboards.map(({ id, name }) => (
                    <MenuItem
                      key={id}
                      as={Link}
                      to={`dashboard/${id}`}
                      onClick={() => setOpen(false)}
                      data-testid="dashboard-button"
                      value={id}
                      {...(activeId === id && { 'data-state': 'checked' })}
                    >
                      {activeId === id ? <IconSelected /> : <IconDashboard />}
                      <Truncate>{name}</Truncate>
                      <MenuItemActions>
                        <Tooltip content="Rename">
                          <IconButton
                            aria-label="Edit dashboard"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              onClickEdit({ id, name });
                            }}
                          >
                            <IconEdit />
                          </IconButton>
                        </Tooltip>
                        <Tooltip content="Delete">
                          <IconButton
                            aria-label="Delete dashboard"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              onClickDelete({
                                id,
                                name,
                                project: project.name,
                              });
                            }}
                          >
                            <IconDelete />
                          </IconButton>
                        </Tooltip>
                      </MenuItemActions>
                    </MenuItem>
                  ))}
              </ComboboxGroup>
            ))
          ) : (
            <ComboboxGroup>
              <GroupLabel>No matching dashboards</GroupLabel>
            </ComboboxGroup>
          )}
        </MenuWrapper>
      </ComboboxProvider>
    </Wrapper>
  );
}
